import { create } from 'zustand';

/**
 * ePCR workflow navigation layout preference.
 *
 * Three distinct options the user can pick between. Persists to
 * localStorage so a chosen layout survives reloads.
 *
 *   - "rail"    : Left-side vertical numbered stepper. Wizard-like but
 *                 always visible. Shows completion % + validation state
 *                 per section, grouped into phases.
 *   - "grid"    : Tile-board launcher. Each section is a tile with
 *                 validation and field-count badges. Tap a tile →
 *                 full-screen that section + a back-to-grid control.
 *   - "command" : Right-side floating 56-px icon rail. Always on top of
 *                 the content, right-thumb-reach on a held tablet.
 *                 Icons only; section label shows on long-press.
 */

export type EpcrLayoutMode = 'rail' | 'grid' | 'command';

const STORAGE_KEY = 'emspcr-epcr-layout';

interface UILayoutState {
  epcrLayout: EpcrLayoutMode;
  setEpcrLayout: (mode: EpcrLayoutMode) => void;
  /** Side nav collapsed state. Auto-collapses when the user enters
   *  an ePCR or a fire report so the content area has full width;
   *  manually toggleable via the AppShell header button. */
  navCollapsed: boolean;
  setNavCollapsed: (v: boolean) => void;
  toggleNav: () => void;
  init: () => void;
}

function readFromStorage(): EpcrLayoutMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'rail' || raw === 'grid' || raw === 'command') return raw;
  } catch { /* ignore */ }
  return 'rail';
}

export const useUILayoutStore = create<UILayoutState>((set, get) => ({
  epcrLayout: 'rail',
  navCollapsed: false,
  setEpcrLayout: (mode) => {
    set({ epcrLayout: mode });
    try { localStorage.setItem(STORAGE_KEY, mode); } catch { /* ignore */ }
  },
  setNavCollapsed: (v) => set({ navCollapsed: v }),
  toggleNav: () => set({ navCollapsed: !get().navCollapsed }),
  init: () => {
    set({ epcrLayout: readFromStorage() });
  },
}));
